/**
 * One skill — the reference `ToolEditor.tsx`, whole (items BD + BE).
 *
 * The layout is the reference's editor, entered when a skill is opened: a
 * header row (back, glyph, name — editable in Edit mode — and the actions:
 * enabled switch, Edit/Update, Publish, Delete), then three columns — details
 * (description, Parameters/History, Run) on the left, the **Edit-with-AI
 * chat** in the centre (swapped for the code view by *View code*), and
 * **Versions** on the right, where clicking a non-current version *switches*
 * to it.
 *
 * Two locks the reference learned the hard way, kept:
 *
 *   - **Everything mutation-racy freezes while the editor is busy** — a regen
 *     stream, a pending approval, a restore, or the post-commit working-copy
 *     refresh. Run against a stale schema, an Edit over an incoming commit, a
 *     Publish of a half-written version: each is that race.
 *   - **The Run button is where the instance gate is visible** — disabled with
 *     the reason, never hidden. The chat's composer carries the same gate with
 *     the same wording, because editing tests against the skill's document.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocale } from "../../i18n/LocaleProvider";
import {
  buildRunParameters,
  canEditSkill,
  canPublishSkill,
  canRunSkill,
  describeTarget,
  enumOptions,
  instanceYear,
  missingYear,
  parameterLabel,
  parameterLabels,
  parameterLabelUpdate,
  skillKey,
  VENDOR_NAMES,
  withLastUsed,
  type ParameterSchemaItem,
  type RunBlock,
  type Skill,
  type SkillDetail as SkillDetailData,
  type SkillsClient,
  SkillsError,
} from "../../lib/skills";
import type { Selection } from "../../lib/selectedInstance";
import { getSkillCategory } from "../../lib/skillCategory";
import { supportsReasoningEffort, type AgentMode } from "../../lib/models";
import { useReasoningEffort } from "../../hooks/useReasoningEffort";
import { EDITOR_TABS_QUERY, useMediaQuery } from "../../hooks/useMediaQuery";
import { useModels } from "../../hooks/useModels";
import { useSkillEdit } from "../../hooks/useSkillEdit";
import type { SkillEditSessions } from "../../lib/skillEditSessions";
import { Modal } from "../settings/Modal";
import { CategoryGlyph } from "./SkillCard";
import { CodeView } from "./CodeView";
import { SkillEditorChat } from "./SkillEditorChat";
import { SkillHistoryPanel } from "./SkillHistoryPanel";
import { SkillVersionsPanel, formatDate } from "./SkillVersionsPanel";
import { Toast } from "../Toast";
import {
  AlertIcon,
  BoxIcon,
  ChevronLeftIcon,
  ClockIcon,
  LayersIcon,
  LoaderIcon,
  PencilIcon,
  PlayIcon,
  SaveIcon,
  TrashIcon,
  UploadIcon,
} from "./icons";

type DetailsTab = "parameters" | "history";
/** The editor's columns, as tabs below `xl` — the reference's mobile layout. */
type EditorTab = "details" | "center" | "versions";

export function SkillDetail({
  skill,
  client,
  selection,
  orgId,
  token,
  userId,
  projectId,
  mayRun,
  mayDelete,
  mayEdit,
  mayPublish,
  onBack,
  onDelete,
  onRan,
  onChanged,
  onPublished,
  onReopen,
  skillEdits,
}: {
  /** Every skill's Edit-with-AI conversation, held by the shell (#196). */
  skillEdits: SkillEditSessions;
  skill: Skill;
  client: SkillsClient;
  selection: Selection;
  orgId: string | null;
  token: string | null;
  userId: string | null;
  projectId: string | null;
  mayRun: boolean;
  mayDelete: boolean;
  mayEdit: boolean;
  mayPublish: boolean;
  onBack: () => void;
  onDelete: () => Promise<void>;
  /** The list's `createdAt` moves with every run. */
  onRan: (executionAt: string) => void;
  /** The skill's name/description/version changed on disk — reload the list. */
  onChanged: () => void;
  /**
   * A new cloud copy exists — reload the **shared** list too.
   *
   * Separate from `onChanged` on purpose. That one fires for a rename, a restore and every
   * commit, and the shared list is the expensive fetch of the two: api-v2 resolves each distinct
   * publisher with its own admin round-trip, so refetching it on a rename would pay that for
   * nothing. Publishing is the one action that adds a row to it.
   */
  onPublished: () => void;
  /**
   * AutoCAD: show the skill at another of its year folders — after a version switch removed
   * the one open now (restore is a checkout). The list reloads first, so the row exists.
   */
  onReopen?: (key: string) => Promise<void>;
}) {
  const { t, locale } = useLocale();
  const [detail, setDetail] = useState<{ kind: "loading" } | { kind: "error"; message: string } | { kind: "loaded"; data: SkillDetailData }>({ kind: "loading" });
  const [detailsTab, setDetailsTab] = useState<DetailsTab>("parameters");
  const [schema, setSchema] = useState<ParameterSchemaItem[]>(() => withLastUsed(skill.parameterSchema, skill.lastUsedParameters));
  const [running, setRunning] = useState(false);
  const [runError, setRunError] = useState<string | null>(null);
  const [missing, setMissing] = useState<string[]>([]);
  const [showCode, setShowCode] = useState(false);
  const [viewingVersion, setViewingVersion] = useState<number | null>(null);
  const [versionCode, setVersionCode] = useState<string | null>(null);
  /** A version's code is being fetched — the code panel shows a loading overlay, not a blink. */
  const [versionLoading, setVersionLoading] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmPublish, setConfirmPublish] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [notice, setNotice] = useState<{ tone: "ok" | "err"; text: string } | null>(null);
  const [historyKey, setHistoryKey] = useState(0);
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState(skill.displayName);
  const [editDesc, setEditDesc] = useState(skill.description);
  const [editParamLabels, setEditParamLabels] = useState(() => parameterLabels(skill.parameterSchema));
  const [saving, setSaving] = useState(false);
  const [agentMode, setAgentMode] = useState<AgentMode>("hitl");
  /** A version switch left the skill without the connected AutoCAD's year: the modal's facts. */
  const [versionGone, setVersionGone] = useState<{ version: number; year: string; versions: string[] } | null>(null);
  /**
   * An AutoCAD skill: the years it supports (its year folders), and the selected AutoCAD's
   * year when the skill does not support it — then running and editing are off, and the
   * supported-versions strip offers a cross-version build for that year (owner, 2026-10-24).
   */
  const isAutocad = skill.target.kind === "autocad";
  const years = isAutocad ? (skill.versionFolders ?? [skill.target.version]) : [];
  const selectedAutocadYear = selection.instance?.host_kind === "autocad" ? instanceYear(selection.instance) : "";
  const unsupportedYear = isAutocad ? missingYear(years, selectedAutocadYear) : null;
  // Below `xl` the three columns become tabs (the reference ToolEditor's
  // `xl:hidden` layout): Details · Chat/Code · Versions, one at a time.
  const tabbed = useMediaQuery(EDITOR_TABS_QUERY);
  const [editorTab, setEditorTab] = useState<EditorTab>("details");

  const { models, current: currentModel, select: selectModel, contextWindow } = useModels(orgId, token);
  // The reasoning chip (#111): the same remembered value the chat pane uses.
  const [reasoningEffort, setReasoningEffort] = useReasoningEffort();
  const reasoningApplies = supportsReasoningEffort(currentModel);

  const loadDetail = useCallback(async () => {
    const data = await client.get(skill.id);
    setDetail({ kind: "loaded", data });
    setSchema(withLastUsed(data.parameterSchema, data.lastUsedParameters));
    setEditName(data.displayName);
    setEditDesc(data.description);
    setEditParamLabels(parameterLabels(data.parameterSchema));
  }, [client, skill.id]);

  useEffect(() => {
    let cancelled = false;
    setDetail({ kind: "loading" });
    client
      .get(skill.id)
      .then((data) => {
        if (cancelled) return;
        setDetail({ kind: "loaded", data });
        setSchema(withLastUsed(data.parameterSchema, data.lastUsedParameters));
        setEditName(data.displayName);
        setEditDesc(data.description);
        setEditParamLabels(parameterLabels(data.parameterSchema));
      })
      .catch((error: unknown) => {
        if (!cancelled) setDetail({ kind: "error", message: error instanceof Error ? error.message : t("toast.skillLoadError") });
      });
    return () => {
      cancelled = true;
    };
  }, [client, skill.id]);

  useEffect(() => {
    if (viewingVersion === null) {
      setVersionCode(null);
      setVersionLoading(false);
      return;
    }
    let cancelled = false;
    // The previous version's code stays on screen under the overlay until this lands, so the
    // panel never renders empty between two versions.
    setVersionLoading(true);
    client
      .version(skill.id, viewingVersion)
      .then((files) => {
        if (!cancelled) setVersionCode(files.code);
      })
      .catch(() => {
        if (!cancelled) setVersionCode(null);
      })
      .finally(() => {
        if (!cancelled) setVersionLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [client, skill.id, viewingVersion]);

  useEffect(() => {
    if (!notice) return;
    const id = window.setTimeout(() => setNotice(null), 3500);
    return () => window.clearTimeout(id);
  }, [notice]);

  // --- the edit chat ---------------------------------------------------------------

  const editVerdict = useMemo(() => canEditSkill(skill, selection), [skill, selection]);
  /**
   * The skill's year is not the selected instance's — **said once, on the title row** (owner).
   *
   * The same sentence was under the Run button and under the composer at the same time, because
   * the run gate and the edit gate each explain their own refusal and this refusal is common to
   * both. It is a fact about the page, not about either control: the skill targets one product
   * year and the instance card has another selected. So the notice moves up beside the title, and
   * the two controls keep only what is theirs to say.
   *
   * Read from the edit gate rather than the run gate because it does not depend on a permission
   * or on the skill being enabled: the instance rule is identical in both, and this way the
   * notice appears for a reader who cannot run.
   */
  const targetMismatch = !editVerdict.ok && editVerdict.block === "wrong-instance";
  const mismatchText = targetMismatch
    ? t("skills.targetMismatch")
        .replace(/\{target\}/g, describeTarget(skill.target, t("skills.unknownVersion")))
        .replace(
          "{selected}",
          selection.instance
            ? selectedInstanceLabel(selection)
            : "",
        )
    : "";
  const editTarget = mayEdit && editVerdict.ok ? editVerdict.instance.sessionId : null;

  const onCodeUpdated = useCallback(
    ({ version, openVersion, versionFolders }: { version: number; openVersion?: string; versionFolders?: string[] }) => {
      void (async () => {
        /**
         * A switch to a version that predates the connected AutoCAD's year (owner, 2026-10-24):
         * cross-built to 2025 and 2027, AutoCAD 2027 connected, switched back to before the
         * build — the skill now has only 2025. Reopening it here would leave an editor that
         * can neither run nor edit, with the picker locked to a year that may not be open. So a
         * modal says why, and dismissing it reloads the list (the skill leaves it: it no longer
         * supports the connected year) and returns there. Reloading first would drop this row
         * — and the modal with the view — before it could be read.
         */
        if (isAutocad && versionFolders && selectedAutocadYear && !versionFolders.includes(selectedAutocadYear)) {
          setVersionGone({ version, year: selectedAutocadYear, versions: versionFolders });
          return;
        }
        // A switch to a version without the open year folder: reopen at the year the agent
        // named (owner, 2026-10-24). This view's folder is gone, so there is nothing to reload.
        if (isAutocad && openVersion && openVersion !== skill.target.version && onReopen) {
          await onReopen(skillKey(skill.baseId, openVersion));
          return;
        }
        try {
          await loadDetail();
        } catch {
          /* the next open reloads */
        }
        onChanged();
        setViewingVersion(null);
        setNotice({
          tone: "ok",
          text: t("skillVersions.switchedDescription").replace("{version}", String(version)),
        });
      })();
    },
    [loadDetail, onChanged, t, isAutocad, onReopen, skill.target.version, skill.baseId, selectedAutocadYear],
  );

  const edit = useSkillEdit({
    skillId: skill.id,
    skillLabel: skill.displayName,
    sessions: skillEdits,
    client,
    organizationId: orgId,
    userId,
    projectId,
    token,
    targetSessionId: editTarget,
    agentMode,
    model: currentModel,
    // Sent only when the chip is shown, as the chat pane does (ThreadsPane): a
    // model that does not reason gets no effort.
    ...(reasoningApplies ? { reasoningEffort } : {}),
    onCodeUpdated,
    t,
  });

  // The commit toast: version + the agent's own one-line summary + the hint.
  useEffect(() => {
    if (!edit.commitNotice) return;
    const lines = [
      t("skillRegenerate.successDescription").replace("{version}", String(edit.commitNotice.version)),
      edit.commitNotice.summary,
      t("skillRegenerate.testHint"),
    ].filter(Boolean);
    setNotice({ tone: "ok", text: lines.join(" — ") });
    edit.clearCommitNotice();
  }, [edit.commitNotice, edit.clearCommitNotice, t]);

  /**
   * Publishing needs the cloud copy to be **yours** (owner) — disabled, not hidden, because the
   * reason is worth reading: a skill downloaded from a colleague cannot be published back over
   * theirs, and a hidden button reads as a missing permission instead.
   */
  const ownsCloudCopy = canPublishSkill(skill, userId);

  /** A skill-mutating op is in flight — lock Run / Edit / Publish / Delete / switch. */
  const skillUpdating = saving || edit.busy || edit.interrupt !== null;

  /**
   * Why the edit chat is closed, if it is — **a run in progress included** (owner).
   *
   * A run and an edit turn both act on the working copy, from opposite ends: the run executes
   * the code on disk, the edit turn rewrites it and commits a new version. Overlapping them
   * means a run whose code changed underneath it, and a commit whose parent is whatever the run
   * happened to leave. The Run button already refuses while an edit turn is live; this is the
   * same gate in the other direction.
   *
   * Ordered so the steadier reason wins: no permission and a wrong instance are facts about the
   * skill, while a run is over in a moment.
   */
  const chatBlocked = !mayEdit
    ? t("skills.edit.noPermission")
    : unsupportedYear
      ? t("skills.crossVersion.chatBlocked").replace(/\{version\}/g, unsupportedYear)
      : targetMismatch
      ? t("skills.edit.mismatchShort")
      : !editVerdict.ok
        ? editBlockReason(editVerdict.block, skill, selection, t)
        : saving
          ? t("toolEditor.saving")
          : running
            ? t("skills.edit.runInProgress")
            : null;

  // --- run --------------------------------------------------------------------------

  const verdict = useMemo(() => canRunSkill(skill, selection, mayRun), [skill, selection, mayRun]);
  const category = getSkillCategory(skill);
  const code = versionCode ?? (detail.kind === "loaded" ? detail.data.code : "");

  const setParam = (index: number, value: unknown) => {
    setSchema((prev) => prev.map((p, i) => (i === index ? { ...p, default: value } : p)));
    setMissing([]);
    setRunError(null);
  };

  const run = async () => {
    if (!verdict.ok || skillUpdating) return;
    const built = buildRunParameters(schema, isAutocad ? "autocad" : "revit");
    if (!built.ok) {
      setMissing(built.missing);
      setRunError(built.error ?? null);
      return;
    }
    setRunning(true);
    setRunError(null);
    try {
      const outcome = await client.execute(skill.id, {
        targetSessionId: verdict.instance.sessionId,
        parametersJson: built.parametersJson,
        ...(skill.autoTransaction !== undefined ? { autoTransaction: skill.autoTransaction } : {}),
      });
      // The old ToolEditor's rule: a run is one more History record — refresh
      // the list and bring the tab forward; the data shows when the record is
      // opened, never inline under the button (owner, 2026-08-25).
      onRan(outcome.executionAt);
      setHistoryKey((k) => k + 1);
      setDetailsTab("history");
      if (outcome.success) setNotice({ tone: "ok", text: t("toast.skillRunSuccess").replace("{name}", skill.displayName) });
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      setRunError(error instanceof SkillsError && error.detail === "host_mismatch" ? message : t("toast.skillRunError").replace("{error}", message));
      setHistoryKey((k) => k + 1);
    } finally {
      setRunning(false);
    }
  };

  // --- header actions ---------------------------------------------------------------

  const remove = async () => {
    setDeleting(true);
    try {
      await onDelete();
    } catch (error) {
      setDeleting(false);
      setConfirmDelete(false);
      setNotice({ tone: "err", text: error instanceof Error ? error.message : t("toast.skillDeleteError") });
    }
  };

  const loadedName = detail.kind === "loaded" ? detail.data.displayName : skill.displayName;
  const loadedDesc = detail.kind === "loaded" ? detail.data.description : skill.description;
  const persistedSchema = detail.kind === "loaded" ? detail.data.parameterSchema : skill.parameterSchema;
  const schemaUpdate = parameterLabelUpdate(persistedSchema, editParamLabels);
  const hasUnsaved = editing && (editName.trim() !== loadedName || editDesc.trim() !== loadedDesc || schemaUpdate !== undefined);

  const cancelEdit = () => {
    setEditName(loadedName);
    setEditDesc(loadedDesc);
    setEditParamLabels(parameterLabels(persistedSchema));
    setEditing(false);
  };

  const saveEdit = async () => {
    if (!mayEdit || detail.kind !== "loaded" || skillUpdating) return;
    if (!hasUnsaved) {
      cancelEdit();
      return;
    }
    setSaving(true);
    try {
      await client.update(skill.id, {
        displayName: editName.trim(), description: editDesc.trim(),
        ...(schemaUpdate ? { parameterSchema: schemaUpdate } : {}),
      });
      await loadDetail();
      onChanged();
      setEditing(false);
      setNotice({ tone: "ok", text: t("toolEditor.updateSuccess") });
    } catch (error) {
      setNotice({ tone: "err", text: error instanceof Error ? error.message : t("toolEditor.updateError") });
    } finally {
      setSaving(false);
    }
  };

  /**
   * The cross-version build (2.x's Revit button, for AutoCAD): an Edit-with-AI turn of its
   * own, started from a year the skill supports on that year's AutoCAD — the same instance
   * the edit chat uses — and the agent asks which years to add (owner, 2026-10-24).
   */
  const crossBuildBlocked = !mayEdit || editTarget === null || skillUpdating || running;
  const startCrossBuild = () => {
    if (crossBuildBlocked) return;
    setShowCode(false);
    if (tabbed) setEditorTab("center");
    edit.buildCrossVersion(t("skills.crossVersion.prompt"));
  };

  const publish = async () => {
    setConfirmPublish(false);
    setPublishing(true);
    try {
      await client.publish(skill.id);
      await loadDetail();
      // The version rows carry the PUBLISHED tag, and publishing is the one thing that moves
      // it without going through `useSkillEdit` — so the list has to be told (owner).
      edit.refreshVersions();
      onChanged();
      // And the Organization tab, which was fetched before this row existed: without this the
      // skill you just published is missing from the list of what the organization shares until
      // the vendor or the tab changes (owner).
      onPublished();
      setNotice({ tone: "ok", text: t("toolEditor.publishSuccess") });
    } catch (error) {
      setNotice({ tone: "err", text: `${t("toolEditor.publishError")}: ${error instanceof Error ? error.message : String(error)}` });
    } finally {
      setPublishing(false);
    }
  };

  // --- render -----------------------------------------------------------------------

  const detailsColumn = (
    <div className="skill-editor-details">
      <div className="skill-desc-card">
        <h4>{t("toolEditor.description")}</h4>
        {editing ? (
          <textarea
            className="settings-input settings-textarea"
            value={editDesc}
            rows={4}
            placeholder={t("toolEditor.descriptionPlaceholder")}
            onChange={(e) => setEditDesc(e.target.value)}
          />
        ) : (
          <p className="skill-desc-text">{loadedDesc || t("toolEditor.noDescription")}</p>
        )}
        {/* Two rows (owner, 2026-10-24): what the skill runs on, then when it last ran. An
            AutoCAD skill names every year it supports ("AutoCAD 2025 · 2027") where a Revit
            skill names its one ("Revit 2024"), so the first row can be long and gets its own. */}
        <div className="skill-desc-meta">
          <div className="skill-card-meta skill-desc-meta-row">
            <BoxIcon size={12} />
            <span title={isAutocad ? t("skills.autocad.years") : undefined}>
              {isAutocad ? `AutoCAD ${years.join(" · ")}` : describeTarget(skill.target, t("skills.unknownVersion"))}
            </span>
          </div>
          <div className="skill-card-meta skill-desc-meta-row">
            <ClockIcon size={12} />
            <span>{t("toolEditor.executedAt")} {formatDate(skill.createdAt, locale)}</span>
            {skill.isPublished ? <span className="skill-badge is-published">{t("skills.published")}</span> : null}
          </div>
        </div>
      </div>

      <div className="skill-tabs" role="tablist">
        {([
          ["parameters", `${t("toolEditor.parameters")}${schema.length ? ` ${schema.length}` : ""}`],
          ["history", t("skillHistory.title")],
        ] as Array<[DetailsTab, string]>).map(([id, label]) => (
          <button key={id} type="button" role="tab" aria-selected={detailsTab === id} className={`skill-tab${detailsTab === id ? " is-active" : ""}`} onClick={() => setDetailsTab(id)}>
            {label}
          </button>
        ))}
      </div>

      <div className="skill-tab-body">
        {detailsTab === "parameters" ? (
          detail.kind === "loading" ? (
            <div className="skills-center muted"><LoaderIcon size={14} /> {t("common.loading")}</div>
          ) : detail.kind === "error" ? (
            <div className="skills-center err">{detail.message}</div>
          ) : schema.length === 0 ? (
            <p className="skills-hint">{t("toolEditor.noParameters")}</p>
          ) : (
            <div className="param-list">
              {schema.map((param, index) => (
                <div className={`param-card${missing.includes(param.name) ? " is-missing" : ""}`} key={`${param.name}-${index}`}>
                  <div className="param-head">
                    <div>
                      {editing && mayEdit ? (
                        <input
                          className="settings-input param-label-input"
                          value={editParamLabels[param.name] ?? ""}
                          placeholder={param.name}
                          aria-label={`${t("toolEditor.parameters")}: ${param.name}`}
                          disabled={saving || skillUpdating}
                          onChange={(e) => setEditParamLabels((prev) => ({ ...prev, [param.name]: e.target.value }))}
                        />
                      ) : <span className="param-name">{parameterLabel(param)}</span>}
                      {param.required ? <span className="param-required">*</span> : null}
                      {param.description ? <p className="param-desc">{param.description}</p> : null}
                    </div>
                    <span className="param-type">{param.type}</span>
                  </div>
                  <ParameterInput param={param} index={index} onChange={(v) => setParam(index, v)} />
                </div>
              ))}
            </div>
          )
        ) : (
          <SkillHistoryPanel client={client} skillId={skill.id} refreshKey={historyKey} />
        )}
      </div>

      <div className="skill-run">
        {missing.length > 0 ? (
          <div className="settings-msg err">
            <strong>{t("toast.missingParams")}</strong> {t("toast.missingParamsDesc").replace("{params}", missing.map((name) => parameterLabel(schema.find((p) => p.name === name) ?? { name })).join(", "))}
          </div>
        ) : null}
        {runError ? <div className="settings-msg err">{runError}</div> : null}
        <button
          type="button"
          className="settings-btn skill-run-btn"
          disabled={!verdict.ok || running || detail.kind !== "loaded" || skillUpdating}
          title={skillUpdating ? t("toolEditor.lockedDuringUpdate") : undefined}
          onClick={() => void run()}
        >
          {running ? <LoaderIcon /> : <PlayIcon />}
          {running ? t("toolEditor.running") : t("toolEditor.run")}
        </button>
        {/**
         * **The reason a run is blocked, and nothing when it is not** (owner).
         *
         * "Runs against 2024_…— Revit 2024" sat under the button whenever the run *was*
         * possible, restating what the instance card already says — and it is the instance card that
         * owns the instance pick, so two places naming it invite the question of which one is
         * in charge. A block is different: it is the reason this button is disabled, it has no
         * other home, and it goes away as soon as it stops being true.
         */}
        {!verdict.ok && !targetMismatch ? (
          <p className="skill-run-reason" data-block={verdict.block}>
            <AlertIcon size={13} /> {blockReason(verdict.block, skill, selection, t)}
          </p>
        ) : null}

      </div>
    </div>
  );

  /**
   * The yellow banner: **only while something is acting on the skill** (owner).
   *
   * It used to stand for the whole time an old version was on screen, and appearing is the
   * problem — it is a block at the top of the panel, so every step into a version pushed the
   * code down and every step out pulled it back up. Switching version to version flashed it
   * twice, once on the way in and once when the switch reset the view to the working copy.
   *
   * What it is actually for is the mismatch it warns about: Run runs the working copy and the
   * edit chat edits the working copy, neither of which is the version being read. That only
   * matters while one of them is in flight, so that is when it shows. Reading an old version
   * quietly needs no warning, and the foot row names the version without moving anything.
   *
   * A **run or an edit turn**, and deliberately not everything `skillUpdating` covers: a
   * restore and the refresh after a commit are both over in a second or two, and a banner that
   * arrives and leaves inside that window is the flash this was meant to remove.
   */
  const versionWarning =
    viewingVersion !== null && (running || edit.streaming || edit.interrupt !== null);

  const codeView = (
    <div className="skill-code">
      {versionWarning ? (
        <div className="skill-code-banner">
          {t("skillVersions.viewingWarning").replace("{version}", String(viewingVersion))}
          <button type="button" className="link-btn" onClick={() => setViewingVersion(null)}>{t("skillVersions.backToCurrent")}</button>
        </div>
      ) : null}
      <CodeView code={code} loading={versionLoading} />
      <div className="skill-code-foot">
        <span>{skill.language}</span>
        <span>•</span>
        <span>{t("toolEditor.lines")}: {code ? code.split("\n").length : 0}</span>
        <span>•</span>
        <span>{t("toolEditor.characters")}: {code.length}</span>
        {/* Which version is on screen, and the way back, in a row that is always here — so
            saying it costs no layout. The banner above appears only when it has a warning to
            make, and this is what replaces it the rest of the time. */}
        {viewingVersion !== null ? (
          <>
            <span>•</span>
            <span className="skill-code-foot-viewing">{t("skillVersions.viewing").replace("{version}", String(viewingVersion))}</span>
            <button type="button" className="link-btn" onClick={() => setViewingVersion(null)}>{t("skillVersions.backToCurrent")}</button>
          </>
        ) : null}
      </div>
    </div>
  );

  const centerColumn = (
    <div className="skill-editor-center">
      {mayEdit && !showCode ? (
        <SkillEditorChat
          parameterSchema={(detail.kind === "loaded" ? detail.data : skill).parameterSchema}
          view={edit}
          token={token}
          blocked={chatBlocked}
          models={models}
          currentModel={currentModel}
          onSelectModel={selectModel}
          contextWindow={contextWindow}
          agentMode={agentMode}
          onAgentMode={setAgentMode}
          reasoningEffort={reasoningApplies ? reasoningEffort : null}
          onReasoningEffort={setReasoningEffort}
          showingCode={false}
          onToggleCode={() => setShowCode(true)}
        />
      ) : (
        <div className="skill-editor-codewrap">
          {mayEdit ? (
            <div className="skill-code-head">
              <span>{t("toolEditor.code")}</span>
              <button type="button" className="settings-btn secondary is-small" onClick={() => setShowCode(false)}>
                <ChevronLeftIcon size={13} />
                {t("skillEdit.backToChat")}
              </button>
            </div>
          ) : null}
          {codeView}
        </div>
      )}
    </div>
  );

  const versionsColumn = (
    <div className="skill-editor-versions">
      <div className="skill-versions-head">{t("skillEdit.versionsTitle")}</div>
      <div className="skill-versions-scroll">
        <SkillVersionsPanel
          client={client}
          skillId={skill.id}
          viewing={viewingVersion}
          onView={(v) => {
            setViewingVersion(v);
            // The centre pane has to show code either way — that is where a version is
            // rendered.
            setShowCode(true);
            /**
             * The tab only when there *are* tabs (#123 §1).
             *
             * Unconditional before: in three-column mode all three are already on
             * screen, so clicking a version moved nothing visible and instead left
             * `editorTab` on `center`. Narrowing the window later then opened on the
             * code tab, a tab the user never chose, with the version they had since
             * stopped looking at. Gated, `editorTab` only ever changes from a tab press,
             * which makes remembering it across a resize correct rather than stale.
             */
            if (tabbed) setEditorTab("center");
          }}
          {...(mayEdit
            ? {
                onSwitch: (v: number) => void edit.switchVersion(v),
                /**
                 * A run blocks the switch too (owner): restoring an older version replaces the
                 * code on disk, and the instance is executing that file. The run would finish
                 * against code nobody chose — half the old version, half the new.
                 */
                switchBlocked: skillUpdating || running || unsupportedYear !== null,
                restoringVersion: edit.restoringVersion,
              }
            : {})}
          refreshKey={edit.versionsKey}
        />
      </div>
    </div>
  );

  return (
    <div className="skill-detail skill-editor" data-skill={skill.id}>
      <div className="skill-detail-head">
        <button type="button" className="icon-btn" onClick={onBack} aria-label={t("common.back")}>
          <ChevronLeftIcon />
        </button>
        <CategoryGlyph category={category.id} />
        <div className="skill-detail-title">
          {editing ? (
            <div className="skill-title-edit">
              <input
                className="settings-input"
                value={editName}
                placeholder={t("toolEditor.namePlaceholder")}
                onChange={(e) => setEditName(e.target.value)}
              />
              {hasUnsaved ? <span className="skill-badge is-unsaved">{t("toolEditor.unsaved")}</span> : null}
            </div>
          ) : (
            // The description lives in the Details column only (owner, 2026-08-25);
            // the old header showed just the name.
            <h3>
              {loadedName}
              {/* AutoCAD skills are in beta: they ship on the new store (owner, 2026-10-24). */}
              {isAutocad ? <span className="app-beta-badge">Beta</span> : null}
            </h3>
          )}
          {/**
           * **Right after the name** (owner), inside the title block rather than across the row
           * from it: it is a note about this skill, and at the far end of a wide row it read as
           * one of the page's controls.
           *
           * It still covers nothing. The name is the part that shrinks — it truncates with an
           * ellipsis — while the chip is `flex: none`, so a long skill name cannot push it into
           * the buttons and the buttons keep their place at the end of the row.
           */}
          {unsupportedYear ? (
            // An AutoCAD skill opened with a year it does not support selected: the same
            // chip, saying which year and — on hover — how to get back to a supported one.
            <p
              className="skill-detail-note"
              role="note"
              title={t("skills.crossVersion.unsupported").replace(/\{version\}/g, unsupportedYear)}
              aria-label={t("skills.crossVersion.unsupported").replace(/\{version\}/g, unsupportedYear)}
            >
              <AlertIcon size={13} />
              <span>{t("skills.crossVersion.unsupportedChip").replace("{version}", unsupportedYear)}</span>
            </p>
          ) : targetMismatch ? (
            <p className="skill-detail-note" role="note" title={mismatchText} aria-label={mismatchText}>
              <AlertIcon size={13} />
              <span>{t("skills.targetMismatchChip").replace("{target}", describeTarget(skill.target, t("skills.unknownVersion")))}</span>
            </p>
          ) : null}
        </div>
        <div className="skill-detail-actions">
          {/* The cross-version build, among the skill's own actions as 2.x's Revit button
              was: from a supported year, on its AutoCAD; the agent asks which years to add. */}
          {isAutocad && mayEdit && !editing ? (
            <button
              type="button"
              className="settings-btn secondary is-small"
              disabled={crossBuildBlocked}
              title={skillUpdating ? t("toolEditor.lockedDuringUpdate") : t("skills.crossVersion.buildOthersHint")}
              aria-label={t("skills.crossVersion.buildOthers")}
              onClick={startCrossBuild}
            >
              <LayersIcon size={13} />
              <span className="btn-label">{t("skills.crossVersion.buildOthers")}</span>
            </button>
          ) : null}
          {mayEdit ? (
            editing ? (
              <>
                {/* Below 1024px the actions are icons with their label as the
                    tooltip (owner, 2026-08-25); wider, the full button. */}
                <button type="button" className="settings-btn secondary is-small" disabled={saving} onClick={cancelEdit} title={t("common.cancel")} aria-label={t("common.cancel")}>
                  <CancelIcon />
                  <span className="btn-label">{t("common.cancel")}</span>
                </button>
                <button type="button" className="settings-btn secondary is-small" disabled={saving || skillUpdating || detail.kind !== "loaded" || !editName.trim()} onClick={() => void saveEdit()} title={saving ? t("toolEditor.saving") : t("toolEditor.update")} aria-label={t("toolEditor.update")}>
                  {saving ? <LoaderIcon size={13} /> : <SaveIcon size={13} />}
                  <span className="btn-label">{saving ? t("toolEditor.saving") : t("toolEditor.update")}</span>
                </button>
              </>
            ) : (
              <button
                type="button"
                className="settings-btn secondary is-small"
                // Off until the detail loads, and for an AutoCAD year the skill does not support (owner, 2026-10-24).
                disabled={skillUpdating || detail.kind !== "loaded" || unsupportedYear !== null}
                title={skillUpdating ? t("toolEditor.lockedDuringUpdate") : t("toolEditor.edit")}
                aria-label={t("toolEditor.edit")}
                onClick={() => setEditing(true)}
              >
                <PencilIcon size={13} />
                <span className="btn-label">{t("toolEditor.edit")}</span>
              </button>
            )
          ) : null}
          {/* Not for an AutoCAD skill yet: the cloud keeps one year per skill and no
              vendor, and an AutoCAD skill spans years. */}
          {mayPublish && !isAutocad ? (
            <button
              type="button"
              className="settings-btn secondary is-small"
              disabled={editing || publishing || skillUpdating || !ownsCloudCopy}
              title={
                !ownsCloudCopy
                  ? t("skills.publishNotOwner")
                  : skillUpdating
                    ? t("toolEditor.lockedDuringUpdate")
                    : publishing
                      ? t("toolEditor.publishing")
                      : t("toolEditor.publish")
              }
              aria-label={t("toolEditor.publish")}
              onClick={() => setConfirmPublish(true)}
            >
              {publishing ? <LoaderIcon size={13} /> : <UploadIcon size={13} />}
              <span className="btn-label">{publishing ? t("toolEditor.publishing") : t("toolEditor.publish")}</span>
            </button>
          ) : null}
          {mayDelete ? (
            <button
              type="button"
              className="settings-btn secondary is-small is-danger"
              disabled={editing || skillUpdating}
              onClick={() => setConfirmDelete(true)}
              aria-label={t("toolEditor.delete")}
              title={skillUpdating ? t("toolEditor.lockedDuringUpdate") : t("toolEditor.delete")}
            >
              <TrashIcon />
              <span className="btn-label">{t("toolEditor.delete")}</span>
            </button>
          ) : null}
        </div>
      </div>


      {/* Bottom right, not a banner under the title (owner): restoring a version, saving and
          publishing are all done from the foot of this page, and a confirmation at the top
          arrives where nobody is looking — after moving the content down to make room for
          itself. */}
      {notice ? <Toast tone={notice.tone} text={notice.text} onClose={() => setNotice(null)} /> : null}

      {tabbed ? (
        <>
          {/* The old web's shadcn `TabsList`: a full-width grey track split into
              equal segments, the active one lifted white — not the underline
              tabs the Details column uses for Parameters · History. */}
          <div className="skill-seg-tabs" role="tablist">
            {([
              ["details", t("toolEditor.details")],
              ["center", mayEdit && !showCode ? t("skillEdit.chatTitle") : t("toolEditor.code")],
              ["versions", t("skillEdit.versionsTitle")],
            ] as Array<[EditorTab, string]>).map(([id, label]) => (
              <button key={id} type="button" role="tab" aria-selected={editorTab === id} className={`skill-seg-tab${editorTab === id ? " is-active" : ""}`} onClick={() => setEditorTab(id)}>
                {label}
              </button>
            ))}
          </div>
          <div className="skill-editor-body is-tabbed">
            {editorTab === "details" ? detailsColumn : editorTab === "center" ? centerColumn : versionsColumn}
          </div>
        </>
      ) : (
        <div className="skill-editor-body">
          {detailsColumn}
          {centerColumn}
          {versionsColumn}
        </div>
      )}

      <Modal
        open={confirmDelete}
        onClose={() => (deleting ? undefined : setConfirmDelete(false))}
        title={t("skills.deleteSkillTitle")}
        description={t("skills.deleteSkillDesc")}
        icon={<span className="modal-icon-disc is-danger"><span className="modal-icon-glyph"><AlertIcon size={18} /></span></span>}
        footer={
          <>
            <button type="button" className="settings-btn secondary" disabled={deleting} onClick={() => setConfirmDelete(false)}>{t("common.cancel")}</button>
            <button type="button" className="settings-btn is-danger" disabled={deleting} onClick={() => void remove()}>
              {deleting ? <LoaderIcon size={14} /> : null} {t("toolEditor.delete")}
            </button>
          </>
        }
      >
        <p className="skills-hint">{skill.displayName}</p>
        {/* A v2 skill is one skill across its years: deleting it removes every year folder. */}
        {isAutocad ? (
          <p className="danger-warning">
            {/* The count and the version list are what is being lost: both in bold. */}
            {t(years.length === 1 ? "skills.deleteOnlyVersion" : "skills.deleteAllVersions")
              .split(/(\{count\}|\{versions\})/)
              .map((part, i) =>
                part === "{count}" ? <strong key={i}>{years.length}</strong>
                : part === "{versions}" ? <strong key={i}>{years.map((y) => `AutoCAD ${y}`).join(", ")}</strong>
                : part,
              )}
          </p>
        ) : null}
      </Modal>

      {/* The switch left no version for the connected AutoCAD: say so, then back to the list —
          the footer button is the only way out. */}
      <Modal
        open={versionGone !== null}
        onClose={() => undefined}
        closable={false}
        showClose={false}
        title={t("skills.versionGone.title").replace("{year}", versionGone?.year ?? "")}
        description={t("skills.versionGone.desc")
          .replace("{version}", String(versionGone?.version ?? ""))
          .replace(/\{year\}/g, versionGone?.year ?? "")
          .replace("{versions}", (versionGone?.versions ?? []).join(", "))}
        icon={<span className="modal-icon-disc is-danger"><span className="modal-icon-glyph"><AlertIcon size={18} /></span></span>}
        footer={
          <button
            type="button"
            className="settings-btn"
            onClick={() => {
              setVersionGone(null);
              onChanged();
              onBack();
            }}
          >
            {t("skills.versionGone.back")}
          </button>
        }
      >
        <p className="skills-hint">{skill.displayName}</p>
      </Modal>

      <Modal
        open={confirmPublish}
        onClose={() => setConfirmPublish(false)}
        title={t("toolEditor.confirmPublishTitle")}
        description={t("toolEditor.confirmPublishDesc")}
        icon={<span className="modal-icon-disc"><span className="modal-icon-glyph"><UploadIcon size={18} /></span></span>}
        footer={
          <>
            <button type="button" className="settings-btn secondary" onClick={() => setConfirmPublish(false)}>{t("common.cancel")}</button>
            <button type="button" className="settings-btn" onClick={() => void publish()}>{t("toolEditor.publish")}</button>
          </>
        }
      >
        <p className="skills-hint">{skill.displayName}</p>
      </Modal>
    </div>
  );
}

function blockReason(block: RunBlock, skill: Skill, selection: Selection, t: (k: string) => string): string {
  const target = describeTarget(skill.target, t("skills.unknownVersion"));
  switch (block) {
    case "no-permission":
      return t("skills.run.noPermission");
    case "disabled":
      return t("skills.run.disabled");
    case "unknown-version":
      return t("skills.run.unknownVersion");
    case "no-instance":
      return t("skills.run.noInstance").replace("{target}", target);
    case "wrong-instance":
      return t("skills.run.wrongInstance")
        .replace("{target}", target)
        .replace("{selected}", selection.instance ? selectedInstanceLabel(selection) : "");
  }
}

/** The chat composer's gate, worded for editing rather than running. */
function editBlockReason(
  block: "unknown-version" | "no-instance" | "wrong-instance",
  skill: Skill,
  selection: Selection,
  t: (k: string) => string,
): string {
  const target = describeTarget(skill.target, t("skills.unknownVersion"));
  switch (block) {
    case "unknown-version":
      return t("skills.edit.unknownVersion");
    case "no-instance":
      return t("skills.edit.noInstance").replace("{target}", target);
    case "wrong-instance": {
      const selected = selection.instance
        ? selectedInstanceLabel(selection)
        : "";
      return t("skills.edit.wrongInstance").replace("{target}", target).replace("{selected}", selected);
    }
  }
}

function ParameterInput({ param, index, onChange }: { param: ParameterSchemaItem; index: number; onChange: (value: unknown) => void }) {
  const { t } = useLocale();
  const value = param.default ?? "";
  const id = `param-${index}`;
  if (param.type === "boolean") {
    const on = value === true || String(value).toLowerCase() === "true";
    return (
      <label className="param-bool" htmlFor={id}>
        <input id={id} type="checkbox" checked={on} onChange={(e) => onChange(String(e.target.checked))} />
        <span>{on ? "True" : "False"}</span>
      </label>
    );
  }
  const options = enumOptions(param);
  if (options.length > 0) {
    return (
      <select id={id} className="settings-input param-input" value={String(value)} onChange={(e) => onChange(e.target.value)}>
        <option value="">{param.description ?? t("toolEditor.selectValue")}</option>
        {options.map((o) => (
          <option key={o} value={o}>{o}</option>
        ))}
      </select>
    );
  }
  if (param.type === "number" || param.type === "integer") {
    return <input id={id} type="number" className="settings-input param-input" value={String(value)} step={param.type === "integer" ? 1 : "any"} min={param.minimum} max={param.maximum} onChange={(e) => onChange(e.target.value)} />;
  }
  const text = typeof value === "string" ? value : JSON.stringify(value);
  // The reference opens a JSON editor for an object-valued string; a
  // monospace textarea carries the same bytes without a CodeMirror bundle.
  if (text.trim().startsWith("{") || text.trim().startsWith("[")) {
    return <textarea id={id} className="settings-input param-input is-json" rows={4} value={text} onChange={(e) => onChange(e.target.value)} spellCheck={false} />;
  }
  return <input id={id} type="text" className="settings-input param-input" value={text} placeholder={param.description ?? ""} onChange={(e) => onChange(e.target.value)} />;
}

/** lucide `X` — the Cancel action's glyph when the header shows icons only. */
function CancelIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M18 6 6 18" />
      <path d="m6 6 12 12" />
    </svg>
  );
}

/** "Revit 2024" / "AutoCAD 2025" — the selected instance, as the mismatch sentences name it. */
function selectedInstanceLabel(selection: Selection): string {
  const instance = selection.instance;
  if (!instance) return "";
  const vendor = VENDOR_NAMES[instance.host_kind as keyof typeof VENDOR_NAMES] ?? instance.host_kind;
  return `${vendor} ${instanceYear(instance)}`;
}
